import { useCallback, useEffect, useRef, useState } from "react";
import {
  describeCepLookupError,
  formatCep,
  isCepLookupCancelled,
  lookupCepAddress,
  normalizeCep,
} from "../services/cep";

export const CEP_LOOKUP_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

const idleState = {
  status: CEP_LOOKUP_STATUS.IDLE,
  address: null,
  error: null,
};

export const useCepLookup = ({ initialCep = "" } = {}) => {
  const initialFormattedCep = formatCep(initialCep);
  const [cep, setCep] = useState(initialFormattedCep);
  const [lookupState, setLookupState] = useState(idleState);
  const cepRef = useRef(initialFormattedCep);
  const requestVersionRef = useRef(0);
  const controllerRef = useRef(null);
  const statusRef = useRef(CEP_LOOKUP_STATUS.IDLE);

  const updateState = useCallback((nextState) => {
    statusRef.current = nextState.status;
    setLookupState(nextState);
  }, []);

  const cancelPendingLookup = useCallback(() => {
    requestVersionRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  const startLookup = useCallback(
    async (value = cepRef.current) => {
      const zipCode = normalizeCep(value);

      if (zipCode.length !== 8) {
        updateState({
          status: CEP_LOOKUP_STATUS.ERROR,
          address: null,
          error: describeCepLookupError({ response: { status: 400 } }),
        });
        return null;
      }

      cancelPendingLookup();
      const requestVersion = requestVersionRef.current;
      const controller = new AbortController();
      controllerRef.current = controller;

      updateState({
        status: CEP_LOOKUP_STATUS.LOADING,
        address: null,
        error: null,
      });

      try {
        const address = await lookupCepAddress(zipCode, {
          signal: controller.signal,
        });

        if (requestVersion !== requestVersionRef.current) {
          return null;
        }

        updateState({
          status: CEP_LOOKUP_STATUS.SUCCESS,
          address,
          error: null,
        });
        return address;
      } catch (error) {
        if (
          requestVersion !== requestVersionRef.current ||
          isCepLookupCancelled(error)
        ) {
          return null;
        }

        updateState({
          status: CEP_LOOKUP_STATUS.ERROR,
          address: null,
          error: describeCepLookupError(error),
        });
        return null;
      } finally {
        if (requestVersion === requestVersionRef.current) {
          controllerRef.current = null;
        }
      }
    },
    [cancelPendingLookup, updateState],
  );

  const setCepValue = useCallback(
    (value) => {
      const formattedCep = formatCep(value);
      const zipCode = normalizeCep(formattedCep);

      cepRef.current = formattedCep;
      setCep(formattedCep);

      if (zipCode.length !== 8) {
        cancelPendingLookup();
        updateState(idleState);
        return formattedCep;
      }

      void startLookup(zipCode);
      return formattedCep;
    },
    [cancelPendingLookup, startLookup, updateState],
  );

  const retry = useCallback(() => startLookup(cepRef.current), [startLookup]);

  const clear = useCallback(() => {
    cancelPendingLookup();
    cepRef.current = "";
    setCep("");
    updateState(idleState);
  }, [cancelPendingLookup, updateState]);

  const handleBlur = useCallback(() => {
    if (
      statusRef.current === CEP_LOOKUP_STATUS.IDLE &&
      normalizeCep(cepRef.current).length === 8
    ) {
      void startLookup(cepRef.current);
    }
  }, [startLookup]);

  useEffect(() => {
    const formattedCep = formatCep(initialCep);

    if (formattedCep === cepRef.current) {
      return;
    }

    cancelPendingLookup();
    cepRef.current = formattedCep;
    setCep(formattedCep);
    updateState(idleState);
  }, [cancelPendingLookup, initialCep, updateState]);

  useEffect(
    () => () => {
      cancelPendingLookup();
    },
    [cancelPendingLookup],
  );

  const isLoading = lookupState.status === CEP_LOOKUP_STATUS.LOADING;

  return {
    cep,
    address: lookupState.address,
    error: lookupState.error,
    status: lookupState.status,
    isLoading,
    isReadOnly: isLoading,
    loadingMessage: isLoading ? "Consultando CEP..." : "",
    setCepValue,
    lookup: startLookup,
    retry,
    clear,
    inputProps: {
      value: cep,
      onChange: (event) => setCepValue(event.target.value),
      onBlur: handleBlur,
      readOnly: isLoading,
      maxLength: 9,
      inputMode: "numeric",
      "aria-busy": isLoading,
    },
    statusProps: {
      role: "status",
      "aria-live": "polite",
    },
  };
};
